/**
 * Interaction events — the deliberate ones, raised by call sites.
 *
 * Unlike session, page-view and error capture, these cannot be collected by
 * a global listener. Automatic click delegation was considered and rejected:
 * it identifies controls by DOM position or label text, both of which a
 * routine copy change silently rewrites, and the resulting funnel breaks
 * without failing anything. An explicit `cta_id` at the call site breaks
 * loudly instead — it shows up in a diff.
 *
 * @module logging/instrumentation/interactions
 */

import type { Outcome } from '../core/types';
import { telemetry } from '../telemetry';

/**
 * Record a call-to-action activation, then flush immediately.
 *
 * The flush is not incidental. A CTA is frequently the last thing that
 * happens on the page — an app-store link, an outbound follow — and an
 * event sitting in a five-second batch window when the page goes away is an
 * event that never existed. Store click-through is the single most valuable
 * number on a marketing site, so it gets the same delivery path as the
 * session-end event: `sendBeacon`, handed to the browser, which completes it
 * whether or not this page is still around.
 *
 * The cost is one request per CTA click rather than a batched one. CTA
 * clicks are rare and high-value, which is exactly the trade worth making;
 * page views stay batched.
 *
 * @param ctaId     - Stable identifier chosen at the call site, e.g.
 *                    'app_store_ios'. Never the button's label, which copy
 *                    changes rewrite.
 * @param placement - Where on the page it was, e.g. 'nav', 'footer'. Its own
 *                    field rather than baked into `ctaId`, so "which CTA"
 *                    and "which placement converts" stay separable.
 */
export function trackCtaClick(ctaId: string, placement: string): void {
  telemetry.emit('web_cta_clicked', { cta_id: ctaId, placement });
  telemetry.flush('navigating');
}

/**
 * Record a completed form submission, successful or not.
 *
 * Field contents are never accepted by this function, and the catalog would
 * strip them if they were passed — only which form, how it ended, and how
 * long it took.
 *
 * @param formId    - Stable form identifier, e.g. 'waitlist'.
 * @param outcome   - How the submission resolved.
 * @param durationMs - Round-trip time, when the call site measured it.
 */
export function trackFormSubmitted(
  formId: string,
  outcome: Outcome,
  durationMs?: number,
): void {
  telemetry.emit('web_form_submitted', {
    form_id: formId,
    outcome,
    ...(durationMs === undefined ? {} : { duration_ms: Math.round(durationMs) }),
  });
}

/** Identifier of the pitch-deck gate, shared by the event and the dashboards. */
export const PITCH_DECK_GATE_FORM_ID = 'pitch_deck_gate';

/**
 * Record an attempt to unlock a password-gated deck.
 *
 * Its own function rather than an optional argument on `trackFormSubmitted`,
 * because this is the only place on the site that reports **who** did
 * something, and a rule that important should be visible at the definition
 * rather than inferred from a call site three files away.
 *
 * The rule, in full:
 *
 * - On success, `recipient` is the deck's per-recipient **watermark** — the
 *   label already rendered across the slides that person is reading. It
 *   answers "did this investor open the deck?", which is the entire reason
 *   a deck is instrumented.
 * - It is **never** the password. The two identify the same recipient, so
 *   nothing is lost analytically, but a password in the log sink is a
 *   working key to the deck handed to every reader of BigQuery, kept
 *   indefinitely.
 * - On failure nothing about the attempt is recorded beyond that it failed.
 *   A string typed into a password box cannot be assumed to belong to this
 *   system — people reuse passwords, and a typo can put someone's unrelated
 *   real credential into the sink permanently.
 *
 * @param recipient - The watermark, when the attempt succeeded. Omitted on
 *                    failure, and callers must not substitute the input.
 */
export function trackDeckGateAttempt(outcome: Outcome, recipient?: string): void {
  telemetry.emit('web_form_submitted', {
    form_id: PITCH_DECK_GATE_FORM_ID,
    outcome,
    ...(outcome === 'succeeded' && recipient ? { deck_recipient: recipient } : {}),
  });
}
